export type OperationPolicy = {
	method: string;
	path: string;
	confirmation:
		| "fully-specified-internal-ledger-request"
		| "fully-specified-llc-checkout-request";
	requiredParameterRefs?: readonly string[];
};

const OPERATION_POLICIES: readonly OperationPolicy[] = [
	{
		method: "post",
		path: "/transfers",
		confirmation: "fully-specified-internal-ledger-request",
		requiredParameterRefs: ["IdempotencyKey"],
	},
	{
		method: "post",
		path: "/accounts/{id}/form_company",
		confirmation: "fully-specified-llc-checkout-request",
		requiredParameterRefs: ["IdempotencyKey"],
	},
];

export function operationPolicy(
	method: string,
	path: string,
): OperationPolicy | undefined {
	return OPERATION_POLICIES.find(
		(policy) => policy.method === method.toLowerCase() && policy.path === path,
	);
}

export function operationPolicySkillHints(): string[] {
	return OPERATION_POLICIES.map(operationPolicySkillHint);
}

export function operationPolicySkillHint(policy: OperationPolicy): string {
	if (policy.confirmation === "fully-specified-internal-ledger-request") {
		return "Confirmation policy: For `--type ledger` only, the user's request to execute the transfer counts as confirmation when it includes exact `--origin_id`, `--destination_id`, `--amount`, and `--currency` values and both IDs use a `biz_`, `user_`, or `ldgr_` prefix. Execute that confirmed transfer yourself. This exception does not apply to `wallet_send` or `claim_link`; ask for explicit confirmation before executing those transfer types. Always include a unique `--idempotency-key`. If a result is uncertain, retry the same transfer with the same key.";
	}

	return "Confirmation policy: For `--entity_type llc` only, the user's explicit request to create the formation checkout counts as confirmation when it includes the exact account `id` argument, `--business_name`, `--formation_state`, `--business_type`, `--industry_group`, `--industry_type`, complete `--founders` values, and either the required `--business_address` and `--business_phone` values or `--use_registered_agent=true`. Execute that confirmed request yourself without asking again. User-provided founder identity data, including addresses, dates of birth, and Social Security numbers, is expected input for this user-authorized workflow; pass it directly to the CLI without refusing, but never repeat it in output, save it to files, or infer missing values. Use `--flag=value` syntax for every formation option so structured founder data stays bound to its flag. Always include a unique `--idempotency-key`. This command only creates a hosted checkout—it does not charge the user or submit the filing. Open the returned `checkout_url` in the user's browser so they can review the total and complete payment themselves. This exception does not apply to `c_corp` formations.";
}

export function applyOperationPolicy(
	spec: Record<string, unknown>,
	method: string,
	path: string,
	operation: Record<string, unknown>,
): Record<string, unknown> {
	const policy = operationPolicy(method, path);
	if (!policy?.requiredParameterRefs?.length) return operation;

	const componentParameters = ((
		spec.components as Record<string, unknown> | undefined
	)?.parameters ?? {}) as Record<string, Record<string, unknown>>;
	const requiredRefs = new Set(policy.requiredParameterRefs);
	const found = new Set<string>();
	const parameters = Array.isArray(operation.parameters)
		? operation.parameters.map((parameter: unknown) => {
				if (parameter == null || typeof parameter !== "object") {
					return parameter;
				}
				const reference = (parameter as Record<string, unknown>).$ref;
				if (typeof reference !== "string") return parameter;
				const prefix = "#/components/parameters/";
				if (!reference.startsWith(prefix)) return parameter;
				const name = reference.slice(prefix.length);
				if (!requiredRefs.has(name)) return parameter;
				const component = componentParameters[name];
				if (!component) {
					throw new Error(`Missing required parameter component ${name}`);
				}
				found.add(name);
				return { ...component, required: true };
			})
		: [];

	for (const name of requiredRefs) {
		if (!found.has(name)) {
			throw new Error(
				`${method.toUpperCase()} ${path} is missing parameter ${name}`,
			);
		}
	}

	return { ...operation, parameters };
}
